import { ImageLoaderConfig } from '@angular/common';

/**
 * Resize service options interface
 */
export interface IResizeOptions {
    width: number;
    height: number;
    strictDims?: boolean;
}

/**
 * Formats safe to hand to an image resizer, lower-case, without the leading dot.
 *
 * `svg` and `gif` are excluded on purpose, matching the long-standing project rule: both
 * render fine in a browser, but rescaling a vector is pointless and rescaling an animated
 * gif drops the animation. `PhotoswipeService` applies the same exclusion.
 *
 * Formats a browser cannot render at all (psd, tiff, ai, ...) are absent for a different
 * reason - they stay downloadable CMS assets and must never reach a resizer.
 *
 * Exported so an implementation can build on it instead of restating the whole set:
 * `protected override readonly resizableExtensions = [...RESIZABLE_IMAGE_EXTENSIONS, 'gif'];`
 */
export const RESIZABLE_IMAGE_EXTENSIONS: readonly string[] = ['jpg', 'jpeg', 'png', 'webp', 'avif'];

/**
 * Resize service interface
 */
export abstract class ResizeService {
    /**
     * Formats this resizer processes.
     *
     * Defaults to the common web set above. Override only when the backend differs -
     * Thumbor, Cloudflare and EdgeIO do not accept the same formats.
     */
    protected readonly resizableExtensions: readonly string[] = RESIZABLE_IMAGE_EXTENSIONS;

    abstract loader(config: ImageLoaderConfig): string;
    abstract resize(imageUrl: string, options: IResizeOptions): string;

    /**
     * Whether this resizer should process the given file.
     *
     * Safe to call with either a relative media path or an absolute URL - only the
     * extension is inspected.
     */
    supportsFormat(url: string | undefined | null): boolean {
        if (!url) {
            return false;
        }

        const fileName = url.split('/').pop() || '';

        if (!fileName.includes('.')) {
            return false;
        }

        return this.resizableExtensions.includes(fileName.split('.').pop()!.toLowerCase());
    }

    /**
     * Whether `src` must bypass the resizer and be served as-is - either because it is
     * remote, or because this resizer does not process that format.
     *
     * Expects the raw source (relative media path or remote URL), not a value already
     * passed through `Url.toMediaUrl()`.
     */
    isDirectSrc(src: string | undefined | null): boolean {
        return !!src && (/^https?:\/\//.test(src) || !this.supportsFormat(src));
    }
}
